import {
  PromotionErrorCode,
  promotionErrorMessage
} from '../../../../helpers/promotion'
import { getTranslate } from '../../../../localization'
import {
  EvaluatedPromotion,
  SkippedPromotion
} from '../../../../types/promotion'

type VerdictState = {
  appliedForCode?: EvaluatedPromotion
  evaluation?: { applied: EvaluatedPromotion[] }
  refusal?: SkippedPromotion
  failed: boolean
  tooLong: boolean
}

/**
 * The one sentence under the field, and whether it reads as an error.
 *
 * Both are derived from the same state in one place so they cannot disagree — before, the
 * helper text and the field's `error` prop each rebuilt the condition independently, which
 * is a drift waiting to happen the next time a state is added.
 *
 * Order matters and is not alphabetical: an applied code beats everything, a service
 * refusal beats our own local checks, and the resting prompt is last because it is what
 * remains when nothing else has an opinion.
 *
 * `reasonText` is the service's own translated sentence, rendered verbatim. Nothing here
 * maps a `reason` to copy: the service distinguishes roughly eighteen reasons and every
 * client-side table collapses them into a handful, which is how one sentence comes to mean
 * a blackout date, a day-of-week rule and an exhausted budget at once.
 */
export function verdictMessage(
  verdict: VerdictState,
  errorCode?: PromotionErrorCode
): { text: string; isError: boolean } {
  // The applied case is deliberately absent. It moved to AppliedOffer, which replaces the
  // field entirely and can state the saving in money — "{name} applied" as helper text
  // under a still-editable input was the weakest thing this section could say at its most
  // important moment. Everything below is a reason the code did NOT apply, so the ordering
  // note above still holds among them.

  if (verdict.refusal) {
    return { text: verdict.refusal.reasonText, isError: true }
  }

  // Our own check, refused before any request was made.
  if (verdict.tooLong) {
    return {
      text: getTranslate(promotionErrorMessage('PROMOTION_CODE_TOO_LONG')),
      isError: true
    }
  }

  // Gated on the same fence as the verdict. Ungated, a failure for one code keeps showing
  // against the next, blaming an unchecked value for a past failure.
  if (verdict.failed) {
    return {
      text: getTranslate(
        'The code could not be checked. You can continue without it.'
      ),
      isError: true
    }
  }

  if (errorCode) {
    return {
      text: getTranslate(promotionErrorMessage(errorCode)),
      isError: true
    }
  }

  if (verdict.evaluation && !verdict.appliedForCode) {
    return {
      text: getTranslate('This code did not reduce the price of this booking.'),
      isError: true
    }
  }

  return {
    text: getTranslate('Enter a code and select Apply to check it.'),
    isError: false
  }
}
